import React, { useState, useRef, useEffect } from 'react';
import posthog from 'posthog-js';
import ParlaySuggestionCard from './ParlaySuggestionCard';
import { ParlaySuggestion } from '../services/api';
import '../styles/ParlaySuggestionsCarousel.css';

interface ParlaySuggestionsCarouselProps {
  suggestions: ParlaySuggestion[];
  onTakeParlay: (suggestion: ParlaySuggestion) => void;
  onParlayPlaced?: (suggestion: ParlaySuggestion) => void;
}

const ParlaySuggestionsCarousel: React.FC<ParlaySuggestionsCarouselProps> = ({ suggestions, onTakeParlay, onParlayPlaced }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [availableSuggestions, setAvailableSuggestions] = useState(suggestions);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Update available suggestions when props change
  React.useEffect(() => {
    setAvailableSuggestions(suggestions);
  }, [suggestions]);

  // Track suggestion viewed when the visible index changes
  useEffect(() => {
    const s = availableSuggestions[currentIndex];
    if (!s) return;
    try {
      posthog.capture('engagement:suggestion_viewed', {
        suggestion_id: `${currentIndex}`,
        total_odds: s.total_odds,
        expected_payout: s.expected_payout,
        leg_count: s.legs?.length || 0,
      });
    } catch {}
  }, [currentIndex, availableSuggestions]);

  if (availableSuggestions.length === 0) {
    return null;
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? availableSuggestions.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === availableSuggestions.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  // Touch event handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && availableSuggestions.length > 1) {
      handleNext();
    }
    if (isRightSwipe && availableSuggestions.length > 1) {
      handlePrevious();
    }
  };

  const handleParlayPlaced = (suggestion: ParlaySuggestion) => {
    // Remove the placed suggestion from available suggestions
    const updatedSuggestions = availableSuggestions.filter(s => s !== suggestion);
    setAvailableSuggestions(updatedSuggestions);
    
    // Adjust current index if needed
    if (currentIndex >= updatedSuggestions.length) {
      setCurrentIndex(updatedSuggestions.length - 1);
    }
    
    // Call parent callback if provided
    if (onParlayPlaced) {
      onParlayPlaced(suggestion);
    }
  };

  return (
    <div className="suggestions-carousel-container">
      <div 
        className="suggestions-carousel"
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="carousel-content">
          <ParlaySuggestionCard
            suggestion={availableSuggestions[currentIndex]}
            onTakeParlay={onTakeParlay}
            onParlayPlaced={handleParlayPlaced}
            currentIndex={currentIndex}
            totalSuggestions={availableSuggestions.length}
            onDotClick={handleDotClick}
          />
        </div>
      </div>
    </div>
  );
};

export default ParlaySuggestionsCarousel;
